"use client";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { 
  Box, Card, CardContent, Typography, 
  IconButton, 
  Button, Divider, Paper, Stack, Tooltip 
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";

import { QK } from "../../../base/qk";
import { deleteFundingProfile, fetchClient, getFundingProfiles } from "../../../api/services/Client";
import useClientDetails from "../../../hooks/useClientDetails";
import FundingModal from "./FundingModal";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";

const Funding = () => {
  const { id: clientID } = useParams();
  const queryClient = useQueryClient();
  
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null); 
  const [deleteID, setDeleteID] = useState(null);

  const { data: fundingProfiles } = useQuery(
    [QK.fundingProfiles, clientID], 
    () => getFundingProfiles(clientID)
  );
  const { fullName } = useClientDetails(clientID);
  const { data: clientDetails } = useQuery(
    [QK.clientDetails, clientID], 
    () => fetchClient(clientID)
  );

  const deleteMutation = useMutation(deleteFundingProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.fundingProfiles, clientID]);
    },
  });

  const handleOpenCreate = () => {
    setSelectedProfile(null); 
    setShowFundingModal(true);
  };

  const handleOpenEdit = (profile) => {
    setSelectedProfile(profile); 
    setShowFundingModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteID(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(deleteID);
    setShowConfirmModal(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalAtmIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                {fullName}'s Funding Profiles
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Manage funding sources and payment configurations
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            startIcon={<AddCircleIcon sx={{ fontSize: 16 }} />} 
            onClick={handleOpenCreate}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 2,
              py: 0.75,
              minHeight: 32
            }}
          >
            Add Profile
          </Button>
        </Box>
        <Divider sx={{ opacity: 0.6 }} />
        
        <CardContent sx={{ p: 2 }}>
          {fundingProfiles?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LocalAtmIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No Funding Profiles
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Add a funding profile to manage payment configurations
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<AddCircleIcon sx={{ fontSize: 16 }} />} 
                onClick={handleOpenCreate}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
              >
                Add First Profile
              </Button>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)' 
              },
              gap: 1.5
            }}>
              {fundingProfiles?.map((profile) => (
                <Paper 
                  key={profile.id} 
                  variant="outlined" 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2,
                    borderColor: '#e2e8f0',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      borderColor: 'primary.main', 
                      bgcolor: '#f8faff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 }}>
                        {profile.service_label || profile.service || 'Service'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {profile.billing_type || 'Not Specified'}
                      </Typography>
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        {profile.funder_1_name || "Private Pay"}
                      </Typography>
                      {profile.billing_type === "Split Funder" && (
                        <Typography variant="caption" color="success.main" fontWeight={500} sx={{ fontSize: '0.6rem' }}>
                          F2: {profile.funder_2_name}
                        </Typography>
                      )}
                      {profile.amount && (
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          £{profile.amount}
                        </Typography>
                      )}
                    </Stack>
                    
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <Tooltip title="Edit" arrow>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleOpenEdit(profile)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': { bgcolor: 'primary.light', color: 'primary.main' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleDeleteClick(profile.id)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': { bgcolor: 'error.light', color: 'error.main' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 180px' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Social Services Ref.
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#0f172a" sx={{ fontSize: '0.8rem' }}>
                  Not Provided
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 180px' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  NHS Number
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#0f172a" sx={{ fontSize: '0.8rem' }}>
                  {clientDetails?.nhs_number || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <FundingModal
        show={showFundingModal}
        onHide={() => setShowFundingModal(false)}
        editData={selectedProfile} 
        clientID={clientID}
      />
      
      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirmDelete}
        title="Delete Funding Profile"
        body="Are you sure you want to remove this funding configuration? This action cannot be undone."
        isLoading={deleteMutation.isLoading}
      />
    </Box>
  );
};

export default Funding;